import { useCallback, useMemo, useState, useEffect } from 'react';

import * as XLSX from 'xlsx';

import {

  Clock,

  PlusCircle,

  CalendarPlus,

  RotateCw,

  FileSpreadsheet,

} from 'lucide-react';

import { observer } from 'mobx-react-lite';

import {

  ODataGrid,

  type ODataGridConfig,

  type GridToolbarHelpers,

} from '@inuxe/prime-odata-grid';

import CommonButton from '../../../components/common-components/button/CommonButton';

import CommonDropdown, {

  Option,

} from '../../../components/common-components/dropdown/CommonDropdown';

import { useStore } from '../../../hooks/useStore';

import type { IOvertimeResult } from '../../../models/overtime/OvertimeModel';

import type { IUserManagementResult } from '../../../models/user-management/UserManagementModel';

import { userManagementService } from '../../../services/modules/userManagement/userManagement.service';

import { buildLegacyGridParams, buildODataMappingFromColumns } from '../../../utils/odataGridUtils';
import { getApprovalStatusRowClassName } from '../../../utils/gridRowClassNames';

import toolbarStyles from '../../../styles/ToolbarButtons.module.css';

import gridStyles from '../../../styles/ODataGridPage.module.css';

import { useOvertimeManagementColumns } from './OvertimeManagement.grid';

import styles from './OvertimeManagement.module.css';

import NewOvertimePopup from './parts/NewOvertimePopup';

import NewLeavePopup from './parts/NewLeavePopup';

import EditOvertimePopup from './parts/EditOvertimePopup';

import RejectionReasonPopup from './parts/RejectionReasonPopup';

import DeleteOvertimeDialog from './parts/DeleteOvertimeDialog';



const GRID_ID = 'overtime-management-grid';



const OvertimeManagement = () => {

  const [viewportWidth, setViewportWidth] = useState<number>(

    typeof window !== 'undefined' ? window.innerWidth : 1200

  );

  const [users, setUsers] = useState<IUserManagementResult[]>([]);

  const { popupStore, overtimeStore, authStore } = useStore();



  useEffect(() => {

    const onResize = () => setViewportWidth(window.innerWidth);

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);

  }, []);



  useEffect(() => {

    const loadUsers = async () => {

      if (authStore.isAdmin) {

        try {

          const usersList = await userManagementService.getAllUsers();

          const actualUsers = Array.isArray(usersList)

            ? usersList

            : (usersList as any)?.value || [];

          setUsers(actualUsers);

        } catch (error) {

          console.error('Error loading users:', error);

        }

      }

    };



    loadUsers();

  }, [authStore.isAdmin]);



  const isMobile = viewportWidth < 768;

  const pageSize = isMobile ? 10 : 20;

  const showStatusColumn = authStore.hasAnyRole(['Admin']);

  const columns = useOvertimeManagementColumns(showStatusColumn);

  const oDataMapping = useMemo(
    () => buildODataMappingFromColumns(columns),
    [columns]
  );

  const gridConfig = useMemo<ODataGridConfig<IOvertimeResult>>(

    () => ({

      gridId: GRID_ID,

      rows: pageSize,

      oDataMapping,

      fetchRows: async params => {

        const result = await overtimeStore.listOvertimes(buildLegacyGridParams(params, { oDataMapping }));

        return { rows: result.rows, totalRecords: result.lastRow };

      },

    }),

    [overtimeStore, pageSize, oDataMapping]

  );



  const onGridReady = useCallback(

    (helpers: GridToolbarHelpers<IOvertimeResult>) => {

      overtimeStore.setGridHelpers(helpers);

    },

    [overtimeStore]

  );



  const exportToExcel = useCallback(

    async (helpers: GridToolbarHelpers<IOvertimeResult>) => {

      try {

        const queryState = helpers.getQueryState({ skip: 0, top: 1000000 });

        const result = await overtimeStore.listOvertimes(buildLegacyGridParams(queryState));



        if (!result?.rows.length) {

          return;

        }



        const exportData = result.rows.map(item => ({

          ID: item.id,

          Kullanıcı: item.userName,

          'Başlangıç Tarihi': new Date(item.startDate).toLocaleString('tr-TR', {

            day: '2-digit',

            month: '2-digit',

            year: 'numeric',

            hour: '2-digit',

            minute: '2-digit',

          }),

          'Bitiş Tarihi': new Date(item.endDate).toLocaleString('tr-TR', {

            day: '2-digit',

            month: '2-digit',

            year: 'numeric',

            hour: '2-digit',

            minute: '2-digit',

          }),

          'Toplam Saat': item.totalHours,

          Açıklama: item.description,

          Tür: item.overtimeType === 'Overtime' ? 'Mesai' : 'İzin',

          Durum:

            item.status === 'Pending'

              ? 'Beklemede'

              : item.status === 'Approved'

                ? 'Onaylandı'

                : 'Reddedildi',

          'Oluşturulma Tarihi': new Date(item.createdDate).toLocaleDateString('tr-TR'),

          Onaylayan: item.approvedBy || '',

          'Onay Tarihi': item.approvedDate

            ? new Date(item.approvedDate).toLocaleDateString('tr-TR')

            : '',

          'Red Sebebi': item.rejectionReason || '',

        }));



        const ws = XLSX.utils.json_to_sheet(exportData);

        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(wb, ws, 'Mesai Kayitlari');

        XLSX.writeFile(wb, `Mesai_Kayitlari_${new Date().toISOString().split('T')[0]}.xlsx`);

      } catch (error) {

        console.error('Excel export error:', error);

      }

    },

    [overtimeStore]

  );



  const handleUserChange = (userId: string | null) => {

    overtimeStore.setSelectedUserId(userId === 'all' ? null : userId);

    overtimeStore.refreshGrid();

  };



  const getRowClassName = (row: IOvertimeResult) => getApprovalStatusRowClassName(row.status);



  const userOptions: Option<string>[] = useMemo(() => {

    const baseOptions: Option<string>[] = [{ value: 'all', label: 'Tüm Kullanıcılar' }];



    if (Array.isArray(users) && users.length > 0) {

      const mappedUserOptions: Option<string>[] = users.map(user => ({

        value: user.id,

        label: `${user.userName}${

          user.firstName && user.lastName ? ` (${user.firstName} ${user.lastName})` : ''

        }`,

      }));

      return [...baseOptions, ...mappedUserOptions];

    }



    return baseOptions;

  }, [users]);



  return (

    <>

      <div className={styles.container}>

        <div className={styles.header}>

          <div className={styles.titleRow}>

            <span className={styles.titleIcon} aria-hidden="true">

              <Clock size={24} strokeWidth={2} />

            </span>

            <div className={styles.titleContent}>

              <h2 className={styles.mainTitle}>Mesai Yönetimi</h2>

              <p className={styles.pageDescription}>

                Mesai ve izin kayıtlarını oluşturun, takip edin ve yönetin.

              </p>

            </div>

          </div>

        </div>



        <div className={styles.gridContainer}>

          <ODataGrid<IOvertimeResult>

            gridId={GRID_ID}

            config={gridConfig}

            columns={columns}

            dataKey="id"

            wrapperClassName={gridStyles.oDataGridPage}

            resizableColumns

            onGridReady={onGridReady}

            rowClassName={getRowClassName}

            toolbarLeft={helpers => (

              <>

                <CommonButton

                  type="primary"

                  onClick={() => popupStore.setIsNewOvertimeOpen(true)}

                  leftIcon={<PlusCircle size={18} strokeWidth={2} />}

                  className={toolbarStyles.toolbarBtnOvertime}

                >

                  Yeni Mesai Kaydı

                </CommonButton>

                <CommonButton

                  type="outline"

                  onClick={() => popupStore.setIsNewLeaveOpen(true)}

                  leftIcon={<CalendarPlus size={18} strokeWidth={2} />}

                  className={toolbarStyles.toolbarBtnLeave}

                >

                  İzin Ekle

                </CommonButton>

                <CommonButton

                  type="tertiary"

                  onClick={() => void exportToExcel(helpers)}

                  leftIcon={<FileSpreadsheet size={18} strokeWidth={2} />}

                  className={toolbarStyles.toolbarBtnExcel}

                >

                  Excel'e Aktar

                </CommonButton>

                <CommonButton

                  type="refresh"

                  onClick={() => helpers.refreshGrid()}

                  leftIcon={<RotateCw size={18} strokeWidth={2} />}

                  className={toolbarStyles.toolbarBtnRefresh}

                >

                  Yenile

                </CommonButton>

              </>

            )}

            toolbarRight={

              authStore.isAdmin ? (

                <div className={styles.userSelector}>

                  <CommonDropdown

                    options={userOptions}

                    value={overtimeStore.selectedUserId ?? 'all'}

                    onChange={handleUserChange}

                    label="Kullanıcı Seçin"

                    className={styles.dropdown}

                  />

                </div>

              ) : null

            }

          />

        </div>

      </div>



      {popupStore.isNewOvertimeOpen && <NewOvertimePopup />}

      {popupStore.isNewLeaveOpen && <NewLeavePopup />}

      {popupStore.isEditOvertimeOpen && <EditOvertimePopup />}

      {popupStore.isRejectionReasonOpen && <RejectionReasonPopup />}

      <DeleteOvertimeDialog />

    </>

  );

};



export default observer(OvertimeManagement);

